import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { RequirementLibraryScreen } from '@/components/requirement-library/requirement-library-screen';
import {
  parseLibrarySearch,
  type LibrarySearch,
} from '@/components/requirement-library/library-logic';

/**
 * Settings -> Requirement library (T-0141 / E11-F01 + E11-F02).
 *
 * The firm's standard requirements: browse and search for every firm
 * user, curation (draft, publish, new module) for curators. Distinct from
 * the Methodology library at `/settings/library`.
 *
 * Design reference: the `#library` screen of
 * `docs/requirement-library/prototype/selection.js`. The screen itself is
 * `components/requirement-library/requirement-library-screen.tsx`; this
 * route only owns the URL state so a view can be shared (E11-F01 notes):
 *
 *   ?q=<search text>&module=<module id>
 *
 * T-0157: `?intake_run=<run id>` opens that AI document intake run in the
 * intake sheet (from the app-wide run watcher's "Open" or a notification),
 * then is dropped from the URL.
 */

export const Route = createFileRoute('/settings/requirement-library')({
  // An empty `module` or `q` is the same as none: all modules, no search.
  validateSearch: (search: Record<string, unknown>): LibrarySearch => parseLibrarySearch(search),
  component: RequirementLibraryRoute,
});

function RequirementLibraryRoute() {
  const { q, module, intake_run: intakeRun } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const onQueryChange = useCallback(
    (next: string) => {
      void navigate({
        search: (prev) => ({ ...prev, q: next.length > 0 ? next : undefined }),
        // Typing must not fill the history with one entry per keystroke.
        replace: true,
      });
    },
    [navigate],
  );
  const onModuleChange = useCallback(
    (next: string | null) => {
      void navigate({ search: (prev) => ({ ...prev, module: next ?? undefined }) });
    },
    [navigate],
  );
  const onOpenRunHandled = useCallback(() => {
    void navigate({ search: (prev) => ({ ...prev, intake_run: undefined }), replace: true });
  }, [navigate]);

  return (
    // Fills the settings pane: viewport minus the 56px top bar and the
    // settings layout's vertical padding.
    <div className="h-[calc(100vh-56px-48px)] min-h-[480px] overflow-hidden rounded-card border border-ink-100 bg-paper-0">
      <RequirementLibraryScreen
        query={q ?? ''}
        moduleId={module ?? null}
        onQueryChange={onQueryChange}
        onModuleChange={onModuleChange}
        openRunId={intakeRun ?? null}
        onOpenRunHandled={onOpenRunHandled}
      />
    </div>
  );
}
